"use client";

import { Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function QuickFaqs() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const faqs = [{
    question: "What Siemens systems do you support?",
    answer: "We specialize in the full range of Siemens Sinumerik systems, including the 808D, 828D, and 840D sl. Our expertise covers everything from initial integration and PLC programming to complex multi-axis retrofitting projects."
  }, {
    question: "How quickly can you supply spare parts?",
    answer: "We maintain a strategic inventory of critical Siemens CNC components at our Gauteng facility. For in-stock items, we offer same-day dispatch. For specialized parts, our direct supply chain ensures the fastest possible lead times to minimize your downtime."
  }, {
    question: "Do you provide onsite field services?",
    answer: "Yes, our technical team provides comprehensive onsite support across South Africa. This includes mechanical repairs, electronic troubleshooting, and system optimizations for Soraluce, Skoda, and other major industrial machine brands."
  }, {
    question: "What is involved in a CNC retrofit?",
    answer: "A typical retrofit involves replacing outdated control systems with modern Siemens hardware. We handle the electrical cabinet redesign, motor and drive installation, and custom PLC development to bring your legacy machines to modern standards."
  }, {
    question: "Are your technicians Siemens certified?",
    answer: "Our engineering team possesses extensive experience and specialized training in Siemens automation technologies. We follow strict industrial protocols to ensure every integration meets global engineering standards for precision and safety."
  }, {
    question: "How do I request a technical quote?",
    answer: "You can contact our experts via phone or email with your machine specifications. For complex retrofitting or field service requests, we typically schedule an onsite assessment to provide a detailed technical and financial proposal."
  }];
  return <section data-section-id="2378" id="quick-faqs" className="bg-background text-foreground py-24 md:py-32 lg:py-40">
      <div className="container mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 lg:gap-12">
          {}
          <div ref={leftRef} className={`lg:col-span-4 mb-16 lg:mb-0 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="lg:sticky lg:top-32">
              
              <h2 className="font-sans font-default text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] tracking-tighter text-primary mb-8">
                Technical<span className="text-primary/80">.</span><br />
                <span className="text-primary">Support.</span>
              </h2>
              <p className="font-sans text-base md:text-[19px] leading-relaxed text-primary mb-10 max-w-md">
                Detailed specifications and operational protocols for our Siemens 
                integration services and industrial part supply. Precision is 
                our baseline standard.
              </p>
              
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-8 lg:col-start-6 transition-all duration-1000 ease-out delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-b border-border last:border-b-0 group">
                  <AccordionTrigger className="py-8 hover:no-underline [&>svg]:hidden font-sans font-default text-xl md:text-[22px] font-bold text-primary leading-snug text-left items-center">
                    <span className="flex-1 pr-6 flex items-center gap-4">
                      <span className="w-1 h-6 bg-primary shrink-0" />
                      {faq.question}
                    </span>
                    <span className="relative flex items-center justify-center size-6 shrink-0 transition-transform duration-300 ease-out group-data-[state=open]:rotate-[135deg]">
                      <Plus className="size-5 text-primary" strokeWidth={2} />
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-8 pt-0 pl-5">
                    <p className="font-sans text-base md:text-[19px] leading-[1.6] text-primary/80 max-w-2xl">
                      {faq.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}